import {
    NumberFormatAmountScale,
    NumberFormatCurrencyPlacement,
    NumberFormatCurrencySymbol,
    NumberFormatNegativeFormat,
    NumberFormatZeroDisplay,
} from "@common/types/reports/number-format-settings";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import {
    ReportSettingLogoOption,
    ReportSettingRowVisibility,
} from "@common/types/reports/report-settings";
import { AccountingReportType } from "@common/types/reports/report-type";
import {
    LAYOUT_FIELD_KEYS,
    LayoutValues,
} from "@common/schemas/domains/reportSettings/layout-setting.schema";

export { LAYOUT_FIELD_KEYS };

export const NUMBER_FORMAT_FIELD_KEYS: ReadonlyArray<keyof NumberFormatValues> =
    [
        "decimalPlaces",
        "negativeFormat",
        "isNegativeInRed",
        "hasThousandsSeparator",
        "zeroDisplay",
        "currencySymbol",
        "currencyPlacement",
        "percentDecimalPlaces",
        "amountScale",
    ];

export const NUMBER_FORMAT_DEFAULTS: NumberFormatValues = {
    decimalPlaces: 2,
    negativeFormat: NumberFormatNegativeFormat.PARENTHESES,
    isNegativeInRed: false,
    hasThousandsSeparator: true,
    zeroDisplay: NumberFormatZeroDisplay.DASH,
    currencySymbol: NumberFormatCurrencySymbol.NONE,
    currencyPlacement: NumberFormatCurrencyPlacement.ALL_ROWS,
    percentDecimalPlaces: 2,
    amountScale: NumberFormatAmountScale.FULL,
};

export const LAYOUT_DEFAULT_FOOTER =
    "Kick.co | {{basis}} Basis | {{generatedDate}} {{generatedTime}}";

/**
 * Code-defined base of every layout resolution chain (D3). Text fields carry
 * `{{token}}` templates so the firm level shows Kick's baseline as inherited;
 * render wiring that turns these into finished chrome is a later phase.
 */
export const getLayoutDefaults = (
    reportType: AccountingReportType,
): LayoutValues => ({
    header: "",
    title: "{{entityName}}",
    subtitle: "{{reportTitle}}",
    subtitle2: "{{reportPeriod}}",
    footer: LAYOUT_DEFAULT_FOOTER,
    memo: "",
    watermarkText: "",
    logoOption: ReportSettingLogoOption.NONE,
    rowVisibility: getDefaultRowVisibility(reportType),
});

const NON_ZERO_DEFAULT_REPORT_TYPES: ReadonlySet<AccountingReportType> =
    new Set([
        AccountingReportType.TRIAL_BALANCE,
        AccountingReportType.CASH_FLOW_STATEMENT,
    ]);

const getDefaultRowVisibility = (
    reportType: AccountingReportType,
): ReportSettingRowVisibility => {
    if (reportType === AccountingReportType.GENERAL_LEDGER) {
        return ReportSettingRowVisibility.GL_STANDARD;
    }
    if (NON_ZERO_DEFAULT_REPORT_TYPES.has(reportType)) {
        return ReportSettingRowVisibility.NON_ZERO;
    }
    return ReportSettingRowVisibility.WITH_ACTIVITY;
};
